export default function createDVTouchModel(THREE) {

  /**
   * @memberof module:THREE
   * @private
   * @class DVTouchModel
   */

  class DVTouchModel extends THREE.Object3D {
    constructor({ controls }) {
      super()
      this.controls = controls
      this.scene = controls.scene
      this.far = 0.1 //The distance when the touch tracking will take over
      this.close = 0.035 //The distance to consider the finger touching
      this.createRayCasters()
    }

    update() {
      const intersecting = this.checkIntersects()
      this.handleIntersect(intersecting)
      this.drawCursor(intersecting)
    }

    updateMatrixWorld(force) {
      super.updateMatrixWorld(force)
      this._updateFingerPointers()
    }

    createRayCasters() {
      this.pointerObject = new THREE.Object3D()
      this.rayObject = new THREE.Object3D()
      this.raycaster = new THREE.Raycaster()
      this.raycaster.far = this.far

      this.rayObject.position.z = 0.02
      try {
        this.add(this.rayObject)
        this.add(this.pointerObject)
      } catch (e) {
        console.log(e)
      }
    }

    _updateFingerPointers() {
      const pointerMatrix = this.rayObject.matrixWorld
      const tempMatrix = new THREE.Matrix4()
      tempMatrix.identity().extractRotation(pointerMatrix)

      this.raycaster.ray.origin.setFromMatrixPosition(pointerMatrix)
      this.raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix)
    }

    checkIntersects() {
      let intersect = null
      try {
        const raycaster = this.raycaster
        const intersectableObjects = [...this.controls.intersectableObjects]
        const cleanedObjects = intersectableObjects.filter(object => object && object.uuid)
        const intersects = raycaster.intersectObjects(cleanedObjects, true)
        if (intersects.length > 0) intersect = intersects[0]
      } catch (e) {
        console.log(e)
      }
      return intersect
    }

    handleIntersect(intersect) {
      if (!intersect) {
        if (this.isTouching) this.dispatchEvent({ type: 'selectEnd', data: { raycaster: this.raycaster, type: 'xr' } });
        this.isTouching = false
      }


      const isHovering = intersect ? intersect.distance < this.far : this.isHovering
      const isTouching = intersect ? intersect.distance < this.close : false

      if (isHovering && !isTouching) {
        //ray move not dragging
        this.dispatchEvent({ type: 'rayMove', data: { raycaster: this.raycaster } })
      }


      if (isHovering && isTouching) {
        //ray move & drag
        let shouldMove = false

        const origin = this.origin || new THREE.Vector3()
        const currentPoint = intersect.point
        const distance = origin.distanceTo(currentPoint)

        if (!this.origin) shouldMove = true
        if (distance > 0.01) shouldMove = true
        if (shouldMove) {
          this.origin = null
          this.dispatchEvent({ type: 'rayMove', data: { raycaster: this.raycaster } })
        }
      }

      if (isHovering && !this.hovering) {
        //start hover
        this.hovering = true
        this.dispatchEvent({ type: 'startHover' })
      }

      if (!isHovering && this.hovering) {
        //end hover
        this.hovering = false
        this.dispatchEvent({ type: 'endHover' })
      }

      if (isTouching && !this.isTouching) {
        //select start
        this.origin = intersect.point
        this.isTouching = true
        this.dispatchEvent({ type: 'selectStart', data: { raycaster: this.raycaster, type: 'xr' } });
      }

      if (!isTouching && this.isTouching) {
        //select end
        this.isTouching = false
        this.dispatchEvent({ type: 'selectEnd', data: { raycaster: this.raycaster, type: 'xr' } });
      }

    }


    drawCursor(intersect) {
      const raycaster = this.raycaster

      let point = new THREE.Vector3()
      let cursorPoint = new THREE.Vector3()
      if (intersect) {
        point = intersect.point
        cursorPoint = raycaster.ray.at(intersect.distance, intersect.point)
      } else {
        cursorPoint = raycaster.ray.at(this.far, point)
      }

      const points = [raycaster.ray.origin, point]

      let rayLine = this.rayLine, cursor = this.cursor

      if (!this.rayLine) {
        rayLine = this.cylinderMesh(points[0], points[1])
        this.rayLine = rayLine
        this.scene.add(rayLine)
      }

      if (!this.cursor) {
        cursor = this.cursorMesh(0.0025)
        this.cursor = cursor
        this.scene.add(cursor)
      }

      rayLine.name = 'desktop-vision-rayline'
      cursor.name = 'desktop-vision-cursor'

      rayLine.visible = this.isHovering
      cursor.visible = this.isHovering

      if (this.isTouching) {
        cursor.material.opacity = 1
        rayLine.material.opacity = 1
      } else {
        cursor.material.opacity = 0.5
        rayLine.material.opacity = 0.5
      }

      this.updateCylinderMesh(this.rayLine, points[0], points[1])
      cursor.position.set(cursorPoint.x, cursorPoint.y, cursorPoint.z)
    }

    cursorMesh(radius) {
      const cursorGeometry = new THREE.SphereGeometry(radius, 10, 10)
      const cursorMaterial = new THREE.MeshBasicMaterial()
      cursorMaterial.transparent = true
      cursorMaterial.opacity = 5
      cursorMaterial.reflectivity = 0
      cursorMaterial.depthWrite = false
      cursorMaterial.depthTest = false

      const cursor = new THREE.Mesh(cursorGeometry, cursorMaterial);  // and do the trick with orienation
      cursor.renderOrder = 100
      cursor.name = 'desktop-vision-cursor'
      return cursor
    }

    cylinderMesh(vstart, vend) {

      var cylGeom = new THREE.CylinderGeometry(0.0005, 0.001, 1, 16);
      cylGeom.translate(0, 0.5, 0);
      cylGeom.rotateX(Math.PI / 2);

      const material = new THREE.MeshBasicMaterial({
        color: 'white',
        opacity: 0.5,
        transparent: true,
        reflectivity: 0
      });

      const cylinder = new THREE.Mesh(cylGeom, material);

      cylinder.name = 'desktop-vision-controller-ray-line'

      cylinder.position.copy(vstart);
      cylinder.lookAt(vend);  // and do the trick with orienation

      return cylinder
    }

    updateCylinderMesh(cylinder, vstart, vend) {
      var cylLength = new THREE.Vector3().subVectors(vend, vstart).length();
      cylinder.scale.z = cylLength
      cylinder.position.copy(vstart);
      cylinder.lookAt(vend);  // and do the trick with orienation
    }
  }

  return DVTouchModel
}